import React, { useState } from "react";
import { Table } from "react-bootstrap";
import { format, parse } from "date-fns";

export default function MembersBusiness({
  search,
  handleInputChange,
  handleSearch,
  handleTabClick,
  provinceList,
}) {
  const [businessTab, setBusinessTab] = useState("members");
  const [
    showBusinessAdditionalFields,
    setShowBusinessAdditionalFields,
  ] = useState(false);
  const [applicationStatus, setApplicationStatus] = useState("all");
  const [timeframe, setTimeframe] = useState(1); // Default to 1 day
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  // Separate pagination states for corporate sections
  const [corporateMembersPagination, setCorporateMembersPagination] = useState({
    currentPage: 0,
    pageSize: 20,
    totalElements: 0,
    totalPages: 0,
    hasNext: false,
    hasPrevious: false,
  });
  const [
    corporateApplicantsPagination,
    setCorporateApplicantsPagination,
  ] = useState({
    currentPage: 0,
    pageSize: 20,
    totalElements: 0,
    totalPages: 0,
    hasNext: false,
    hasPrevious: false,
  });

  // Application search state
  const [applicationSearchResults, setApplicationSearchResults] = useState([]);
  const [applicationSearchLoading, setApplicationSearchLoading] = useState(
    false
  );
  const [hasApplicationSearched, setHasApplicationSearched] = useState(false);
  const [applicationSortConfig, setApplicationSortConfig] = useState({
    sortBy: "created",
    sortDirection: "DESC",
  });

  const handleTimeframeChange = (event) => {
    setTimeframe(parseInt(event.target.value, 10));
  };

  const handleSearchClick = (e) => {
    handleSearch(e);
  };

  const formatDateForDisplay = (dateString) => {
    if (!dateString) return "";
    try {
      const date = parse(dateString, "MM-dd-yyyy", new Date());
      return format(date, "MMMM d");
    } catch (error) {
      return dateString;
    }
  };

  // Function to format timestamp to required format
  const formatApplicationDateTime = (timestamp) => {
    if (!timestamp) return "";
    const date = new Date(timestamp);
    return `${format(date, "MM/dd/yyyy")}\nat\n${format(date, "hh:mm:ss a")}`;
  };

  // Function to determine status based on API response
  const getApplicationStatus = (result) => {
    if (result.status === "Inactive") {
      return { status: "Inactive", className: "text-info" };
    } else if (result.inReviewAt) {
      return { status: "In Review", className: "text-warning" };
    } else if (result.uploadedAt) {
      return { status: "Completed", className: "text-success" };
    } else {
      return { status: "In Progress", className: "text-primary" };
    }
  };

  // Application sort handler
  const handleApplicationSort = (field) => {
    const newDirection =
      applicationSortConfig.sortBy === field &&
      applicationSortConfig.sortDirection === "ASC"
        ? "DESC"
        : "ASC";

    setApplicationSortConfig({
      sortBy: field,
      sortDirection: newDirection,
    });
  };

  // Application sortable header component
  const ApplicationSortableHeader = ({ field, label }) => {
    const getSortIcon = () => {
      if (applicationSortConfig.sortBy !== field) {
        return (
          <i
            className="fas fa-sort"
            style={{ fontSize: "10px", marginLeft: "4px" }}
          ></i>
        );
      }
      return applicationSortConfig.sortDirection === "ASC" ? "▲" : "▼";
    };

    return (
      <th
        onClick={() => handleApplicationSort(field)}
        style={{
          cursor: "pointer",
          userSelect: "none",
          position: "relative",
        }}
        className="sortable-header"
      >
        {label}
        <span style={{ marginLeft: "5px", fontSize: "0.8em", color: "#666" }}>
          {getSortIcon()}
        </span>
      </th>
    );
  };

  const renderBusinessApplications = () => (
    <>
      {/* Search row */}
      <div className="row mb-2 g-2 align-items-end">
        <div className="col-md-3">
          <label className="form-label">Application ID</label>
          <input
            className="form-control"
            name="applicationId"
            placeholder="Application ID"
            value={search.applicationId || ""}
            onChange={handleInputChange}
          />
        </div>
        <div className="col-md-3">
          <label className="form-label">
            Timeframe to filter applications by
          </label>
          <select
            className="form-control"
            value={timeframe}
            onChange={handleTimeframeChange}
          >
            <option value={1}>1 day - (Default)</option>
            <option value={7}>7 days</option>
            <option value={30}>30 days</option>
          </select>
        </div>
        <div className="col-md-3">
          <label className="form-label">From</label>
          <input
            className="form-control"
            placeholder="MM-DD-YYYY"
            value={fromDate}
            disabled
          />
        </div>
        <div className="col-md-3">
          <label className="form-label">To</label>
          <input
            className="form-control"
            placeholder="MM-DD-YYYY"
            value={toDate}
            disabled
          />
        </div>
      </div>
      <div className="row mb-2">
        <div className="col-md-2">
          <button className="btn btn-primary" onClick={handleSearch}>
            SEARCH
          </button>
        </div>
        <div className="col-md-10 d-flex justify-content-end align-items-center">
          <span
            className="ms-4 text-primary d-flex align-items-center"
            style={{
              marginLeft: "auto",
              cursor: "pointer",
              fontSize: "0.95em",
            }}
            onClick={handleSearchClick}
          >
            <i
              className="fas fa-sync mr-1"
              style={{ paddingRight: "0.5rem", marginTop: "4px" }}
            ></i>{" "}
            Refresh Table
          </span>
        </div>
      </div>
      <div className="fw-bold mb-2" style={{ fontSize: "1.1em" }}>
        Displaying <span className="fst-italic">'Business'</span> Applications
        from {formatDateForDisplay(fromDate)} - {formatDateForDisplay(toDate)} (
        {timeframe} day{timeframe !== 1 ? "s" : ""})
      </div>
      {/* Status filter buttons */}
      <div className="mb-3 d-flex gap-2">
        {[
          { label: "All", value: "all" },
          { label: "In Progress", value: "inprogress" },
          { label: "In Review", value: "inreview" },
          { label: "Completed", value: "completed" },
        ].map((btn) => (
          <button
            key={btn.value}
            className={`btn ${
              applicationStatus === btn.value
                ? "btn-dark"
                : "btn-outline-secondary"
            }`}
            style={{ minWidth: 120, marginRight: "1rem" }}
            onClick={() => setApplicationStatus(btn.value)}
            type="button"
          >
            {btn.label}
          </button>
        ))}
      </div>
      <div className="d-flex align-items-center justify-content-between mb-2">
        <div className="d-flex align-items-center" style={{ gap: "0.5rem" }}>
          <span style={{ fontSize: 12 }}>Items per page:</span>
          <select
            className="form-select form-select-sm"
            style={{ width: "60px", fontSize: 12 }}
            value={corporateApplicantsPagination.pageSize}
            onChange={(e) => {
              const newPageSize = parseInt(e.target.value, 10);
              setCorporateApplicantsPagination((prev) => ({
                ...prev,
                pageSize: newPageSize,
                currentPage: 0,
              }));
            }}
          >
            <option>20</option>
            <option>40</option>
          </select>
        </div>
        <div className="d-flex align-items-center" style={{ gap: "0.5rem" }}>
          <select
            className="form-select form-select-sm"
            style={{ width: "50px", fontSize: 12 }}
            value={corporateApplicantsPagination.currentPage + 1}
            onChange={(e) => {
              const newPage = parseInt(e.target.value, 10) - 1;
              setCorporateApplicantsPagination((prev) => ({
                ...prev,
                currentPage: newPage,
              }));
            }}
          >
            {Array.from(
              { length: Math.max(1, corporateApplicantsPagination.totalPages) },
              (_, i) => (
                <option key={i + 1} value={i + 1}>
                  {i + 1}
                </option>
              )
            )}
          </select>
          <span style={{ fontSize: 12 }}>
            of {corporateApplicantsPagination.totalPages || 1}
          </span>
          <button
            className="btn btn-sm btn-outline-secondary"
            style={{ fontSize: 12, minWidth: "32px", height: "32px" }}
            disabled={corporateApplicantsPagination.currentPage === 0}
            onClick={() => {
              if (corporateApplicantsPagination.currentPage > 0) {
                setCorporateApplicantsPagination((prev) => ({
                  ...prev,
                  currentPage: prev.currentPage - 1,
                }));
              }
            }}
          >
            <i className="fas fa-chevron-left"></i>
          </button>
          <button
            className="btn btn-sm btn-outline-secondary"
            style={{ fontSize: 12, minWidth: "32px", height: "32px" }}
            disabled={
              corporateApplicantsPagination.currentPage >=
              corporateApplicantsPagination.totalPages - 1
            }
            onClick={() => {
              if (
                corporateApplicantsPagination.currentPage <
                corporateApplicantsPagination.totalPages - 1
              ) {
                setCorporateApplicantsPagination((prev) => ({
                  ...prev,
                  currentPage: prev.currentPage + 1,
                }));
              }
            }}
          >
            <i className="fas fa-chevron-right"></i>
          </button>
        </div>
      </div>
      <Table striped hover size="sm" style={{ fontSize: 12 }} responsive>
        <thead>
          <tr>
            <ApplicationSortableHeader field="type" label="PRODUCT" />
            <ApplicationSortableHeader
              field="bundleProductId"
              label="PRODUCT ID"
            />
            <ApplicationSortableHeader field="id" label="APPLICATION ID" />
            <ApplicationSortableHeader
              field="applicantId"
              label="APPLICANT ID"
            />
            <ApplicationSortableHeader field="created" label="TIME/DATE" />
            <ApplicationSortableHeader field="status" label="STATUS" />
            <th>
              <i className="fas fa-external-link-alt" />
            </th>
          </tr>
        </thead>
        <tbody>
          {applicationSearchLoading ? (
            <tr>
              <td colSpan={7} className="text-center text-muted py-4">
                <div>
                  <i className="fas fa-spinner fa-spin mr-2"></i>
                  Searching applications...
                </div>
              </td>
            </tr>
          ) : applicationSearchResults.length > 0 ? (
            applicationSearchResults.map((result, idx) => {
              const statusInfo = getApplicationStatus(result);
              return (
                <tr key={result.id || idx}>
                  <td>{result.type || ""}</td>
                  <td>{result.bundleProductId || ""}</td>
                  <td>{result.id || ""}</td>
                  <td>{result.applicantId || ""}</td>
                  <td>{formatApplicationDateTime(result.created)}</td>
                  <td>
                    <span
                      className={statusInfo.className}
                      style={{ fontWeight: 500, marginRight: "3px" }}
                    >
                      {statusInfo.status}
                    </span>
                  </td>
                  <td>
                    <a href="#" className="ms-2 text-primary" title="Open">
                      <i className="fas fa-external-link-alt" />
                    </a>
                  </td>
                </tr>
              );
            })
          ) : hasApplicationSearched ? (
            <tr>
              <td colSpan={7} className="text-center text-muted py-4">
                <div>
                  No applications found matching your search criteria.
                  <br />
                  Try adjusting your search parameters or timeframe.
                </div>
              </td>
            </tr>
          ) : (
            <tr>
              <td colSpan={7} className="text-center text-muted py-4">
                <div>No applications to display.</div>
                <p />
                <div>Input search parameters and hit 'Search'.</div>
              </td>
            </tr>
          )}
        </tbody>
      </Table>
    </>
  );

  // Custom tab bar style for Business (same implementation as personalTabBar)
  const businessTabBar = (
    <div className="branch-subtab-container">
      <div className="tab-wrapper">
        <ul className="nav nav-tabs">
          <li className="nav-item">
            <button
              type="button"
              className={
                businessTab === "members"
                  ? "nav-link active"
                  : "nav-link inactive"
              }
              onClick={() => {
                handleTabClick("business");
                setShowBusinessAdditionalFields(false);
                setBusinessTab("members");
              }}
            >
              Members
            </button>
          </li>
          <li className="nav-item">
            <button
              type="button"
              className={
                businessTab === "applications"
                  ? "nav-link active"
                  : "nav-link inactive"
              }
              onClick={() => {
                handleTabClick("business");
                setBusinessTab("applications");
              }}
            >
              Applications
            </button>
          </li>
        </ul>
      </div>
    </div>
  );

  return (
    <>
      {businessTabBar}
      {businessTab === "applications" ? (
        renderBusinessApplications()
      ) : (
        <>
          {/* Business name search */}
          <div className="mb-2">
            <label className="form-label">Business name</label>
            <input
              className="form-control"
              name="businessName"
              placeholder="Legal first name"
              value={search.businessName || ""}
              onChange={handleInputChange}
            />
            <div
              className="text-muted"
              style={{ fontStyle: "italic", fontSize: "0.95em" }}
            >
              This can be the legally registered name or a registered trade
              name.
            </div>
          </div>
          <div className="[ row ] mb-2">
            <div className="col-xl-6 col-lg-6 col-md-6 col-12">
              <button className="btn btn-primary" onClick={handleSearch}>
                SEARCH
              </button>
            </div>
            <div className="col-xl-6 col-lg-6 col-md-6 col-12 d-flex justify-content-end align-items-center">
              <span
                className="text-danger"
                style={{ cursor: "pointer", fontSize: "1em", fontWeight: 500 }}
                tabIndex={0}
                onClick={() => setShowBusinessAdditionalFields((v) => !v)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    setShowBusinessAdditionalFields((v) => !v);
                  }
                }}
              >
                <i className="fas fa-filter mr-1"></i> Additional Search Fields{" "}
                <i
                  className={
                    showBusinessAdditionalFields
                      ? "fas fa-caret-down ml-1"
                      : "fas fa-caret-right ml-1"
                  }
                ></i>
              </span>
            </div>
          </div>
          {showBusinessAdditionalFields && (
            <div
              style={{
                background: "#f3f3f3",
                borderRadius: 6,
                padding: 32,
                marginBottom: 24,
                border: "1px solid #e0e0e0",
              }}
            >
              <div
                style={{
                  fontWeight: 700,
                  textTransform: "uppercase",
                  marginBottom: 16,
                  fontSize: 18,
                }}
              >
                BUSINESS NUMBER
              </div>
              <div className="row mb-4">
                <div className="col-md-6 mb-3 mb-md-0">
                  <label className="form-label">State / territory</label>
                  <select
                    className="form-control"
                    name="businessState"
                    value={search.businessState || ""}
                    onChange={handleInputChange}
                  >
                    <option value="">Select a state / territory</option>
                    {provinceList.map((province) => {
                      if (province.country === search.country) {
                        return (
                          <option key={province.code} value={province.code}>
                            {province.description}
                          </option>
                        );
                      }
                      return null;
                    })}
                  </select>
                </div>
                <div className="col-md-6">
                  <label className="form-label">Business no.</label>
                  <input
                    className="form-control"
                    name="businessNo"
                    placeholder="eg. 7783765555"
                    value={search.businessNo || ""}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
              <div className="[ divider ]" />
              <div
                style={{
                  fontWeight: 700,
                  textTransform: "uppercase",
                  marginBottom: 16,
                  marginTop: 24,
                  fontSize: 18,
                }}
              >
                AUTHORIZED SIGNATORY
              </div>
              <div className="row">
                <div className="col-md-6 mb-3 mb-md-0">
                  <label className="form-label">First name</label>
                  <input
                    className="form-control"
                    name="signatoryFirstName"
                    placeholder="Legal first name"
                    value={search.signatoryFirstName || ""}
                    onChange={handleInputChange}
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label">Last name</label>
                  <input
                    className="form-control"
                    name="signatoryLastName"
                    placeholder="Legal last name"
                    value={search.signatoryLastName || ""}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
            </div>
          )}
          <div className="[ row ]">
            <div className="col-xl-6 col-lg-6 col-md-6 col-12">
              <span
                className="ms-auto fw-bold"
                style={{
                  cursor: "pointer",
                  fontSize: "0.95em",
                  fontWeight: "bold",
                }}
              >
                Displaying 'Business' Members
              </span>
            </div>
            <div className="col-xl-6 col-lg-6 col-md-6 col-12 d-flex justify-content-end">
              <span
                className="ms-auto text-primary d-flex"
                style={{
                  marginLeft: "auto",
                  cursor: "pointer",
                  fontSize: "0.95em",
                }}
              >
                <i
                  className="fas fa-sync mr-1"
                  style={{ paddingRight: "0.5rem", marginTop: "4px" }}
                ></i>{" "}
                Refresh Table
              </span>
            </div>
          </div>

          <div
            className="d-flex align-items-center justify-content-between mb-2"
            style={{ paddingTop: "1rem" }}
          >
            <div
              className="d-flex align-items-center"
              style={{ gap: "0.5rem" }}
            >
              <span style={{ fontSize: 12 }}>Items per page:</span>
              <select
                className="form-select form-select-sm"
                style={{ width: "60px", fontSize: 12 }}
                value={corporateMembersPagination.pageSize}
                onChange={(e) => {
                  const newPageSize = parseInt(e.target.value, 10);
                  setCorporateMembersPagination((prev) => ({
                    ...prev,
                    pageSize: newPageSize,
                    currentPage: 0,
                  }));
                }}
              >
                <option>20</option>
                <option>40</option>
              </select>
            </div>
            <div
              className="d-flex align-items-center"
              style={{ gap: "0.5rem" }}
            >
              <select
                className="form-select form-select-sm"
                style={{ width: "50px", fontSize: 12 }}
                value={corporateMembersPagination.currentPage + 1}
                onChange={(e) => {
                  const newPage = parseInt(e.target.value, 10) - 1;
                  setCorporateMembersPagination((prev) => ({
                    ...prev,
                    currentPage: newPage,
                  }));
                }}
              >
                {Array.from(
                  {
                    length: Math.max(1, corporateMembersPagination.totalPages),
                  },
                  (_, i) => (
                    <option key={i + 1} value={i + 1}>
                      {i + 1}
                    </option>
                  )
                )}
              </select>
              <span style={{ fontSize: 12 }}>
                of {corporateMembersPagination.totalPages || 1}
              </span>
              <button
                className="btn btn-sm btn-outline-secondary"
                style={{ fontSize: 12, minWidth: "32px", height: "32px" }}
                disabled={corporateMembersPagination.currentPage === 0}
                onClick={() => {
                  if (corporateMembersPagination.currentPage > 0) {
                    setCorporateMembersPagination((prev) => ({
                      ...prev,
                      currentPage: prev.currentPage - 1,
                    }));
                  }
                }}
              >
                <i className="fas fa-chevron-left"></i>
              </button>
              <button
                className="btn btn-sm btn-outline-secondary"
                style={{ fontSize: 12, minWidth: "32px", height: "32px" }}
                disabled={
                  corporateMembersPagination.currentPage >=
                  corporateMembersPagination.totalPages - 1
                }
                onClick={() => {
                  if (
                    corporateMembersPagination.currentPage <
                    corporateMembersPagination.totalPages - 1
                  ) {
                    setCorporateMembersPagination((prev) => ({
                      ...prev,
                      currentPage: prev.currentPage + 1,
                    }));
                  }
                }}
              >
                <i className="fas fa-chevron-right"></i>
              </button>
            </div>
          </div>
          <Table striped hover size="sm" style={{ fontSize: 12 }} responsive>
            <thead>
              <tr>
                <th>
                  BUSINESS NAME{" "}
                  <i
                    className="fas fa-sort"
                    style={{ fontSize: "10px", marginLeft: "4px" }}
                  ></i>
                </th>
                <th>
                  TRADE NAME{" "}
                  <i
                    className="fas fa-sort"
                    style={{ fontSize: "10px", marginLeft: "4px" }}
                  ></i>
                </th>
                <th>
                  REGION{" "}
                  <i
                    className="fas fa-sort"
                    style={{ fontSize: "10px", marginLeft: "4px" }}
                  ></i>
                </th>
                <th>
                  BUSINESS NO.{" "}
                  <i
                    className="fas fa-sort"
                    style={{ fontSize: "10px", marginLeft: "4px" }}
                  ></i>
                </th>
                <th>
                  MEMBER ID #{" "}
                  <i
                    className="fas fa-sort"
                    style={{ fontSize: "10px", marginLeft: "4px" }}
                  ></i>
                </th>
                <th>
                  <i className="fas fa-external-link-alt" />
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td
                  colSpan={6}
                  className="text-center text-muted py-4"
                  style={{ fontSize: "1.2em" }}
                >
                  <div>No business members to display.</div>
                  <div className="mt-2" style={{ fontSize: "1em" }}>
                    Input some search parameters and hit 'Search'.
                  </div>
                </td>
              </tr>
            </tbody>
          </Table>
        </>
      )}
    </>
  );
}
